// Reports — every named list behind the dashboard counts (new / lapsed donors, anniversaries,
// pipeline, operations). Scoped like the dashboard; needs the "View donation" permission.
// URL: /reports?list=<type>&fy=<budgetYearId>&days=<n>

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSpreadsheet, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

import { ApiError, saveBlob } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  downloadInsightListXlsx,
  fetchContextOptions,
  fetchInsightList,
  formatAmount,
  type BudgetYear,
  type InsightColumn,
  type InsightList,
  type InsightListType,
} from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { matchesAllWords } from "@/lib/search";
import { useSessionScope } from "@/lib/session-scope";
import { PageContainer } from "@/components/page";
import { DonorLink } from "@/components/donor-link";
import { EmailValue, PhoneValue } from "@/components/contact-value";
import { BlockPlaceholder, Busy } from "@/components/busy";
import { CauseFilter, useCauseParam } from "@/components/cause-filter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LISTS: { group: string; items: { value: InsightListType; label: string; byDays?: boolean; byFy?: boolean }[] }[] = [
  {
    group: "Donors",
    items: [
      { value: "newDonors", label: "New donors", byFy: true },
      { value: "lapsedDonors", label: "Lapsed donors (gave last FY, not this FY)", byFy: true },
      { value: "anniversaries", label: "Upcoming birthdays & anniversaries", byDays: true },
      { value: "donationAnniversaries", label: "Donation anniversaries (gave a year ago)", byDays: true },
    ],
  },
  {
    group: "Pipeline",
    items: [
      { value: "pendingReceipts", label: "Pending receipts (all years)" },
      { value: "pledgeInstallments", label: "Pledge instalments overdue / due in 30 days" },
      { value: "recurringMissed", label: "Missed recurring instalments", byFy: true },
    ],
  },
  {
    group: "Operations",
    items: [
      { value: "chequesUndeposited", label: "Cheques not yet deposited", byFy: true },
      { value: "eightyGNoPan", label: "80G receipts without PAN", byFy: true },
      { value: "tallyPending", label: "Not posted to Tally", byFy: true },
    ],
  },
];
const ALL_ITEMS = LISTS.flatMap((g) => g.items);
const DAY_OPTIONS = [7, 15, 30, 60, 90];

export default function Reports() {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const [params, setParams] = useSearchParams();

  const listType = (ALL_ITEMS.some((i) => i.value === params.get("list")) ? params.get("list") : "newDonors") as InsightListType;
  const fyId = params.get("fy") ? Number(params.get("fy")) : null;
  const days = Number(params.get("days")) || 7;
  const meta = ALL_ITEMS.find((i) => i.value === listType)!;
  const [causeId] = useCauseParam();

  const [years, setYears] = useState<BudgetYear[]>([]);
  const [currentFyId, setCurrentFyId] = useState<number | null>(null);
  const [data, setData] = useState<InsightList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  function update(next: Record<string, string | null>) {
    const q = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v == null ? q.delete(k) : q.set(k, v)));
    setParams(q, { replace: true });
  }

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchContextOptions(token, controller.signal)
      .then((o) => {
        setYears(o.budgetYears);
        setCurrentFyId(o.currentBudgetYearId);
      })
      .catch(() => {
        // FY picker just stays empty; the list still loads with the default FY.
      });
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!token || !scope) return;
    const controller = new AbortController();
    // Keep the previous rows visible (dimmed) while the next list loads.
    setLoading(true);
    setError(null);
    fetchInsightList(token, scope, listType, { budgetYearId: fyId, causeId, days }, controller.signal)
      .then(setData)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      })
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(
          e instanceof ApiError && e.status === 403
            ? 'These lists name donors, so they need the "View donation" permission.'
            : (e as Error).message
        );
      });
    return () => controller.abort();
  }, [token, scope, listType, fyId, causeId, days]);

  const rows = useMemo(() => {
    if (!data) return [];
    const textCols = data.columns.filter((c) => c.type === "text").map((c) => c.key);
    return data.rows.filter((r) => matchesAllWords(query, ...textCols.map((k) => (r[k] == null ? "" : String(r[k])))));
  }, [data, query]);

  const listItems = ALL_ITEMS.map((i) => ({ value: i.value, label: i.label }));
  const fyItems = years.map((y) => ({ value: String(y.id), label: y.name }));
  const dayItems = DAY_OPTIONS.map((d) => ({ value: String(d), label: `Next ${d} days` }));

  const [exporting, setExporting] = useState(false);

  /** Excel export is built on the server (all rows, same filters + search as the screen). */
  async function exportExcel() {
    if (!token || !scope || !data || rows.length === 0) return;
    setExporting(true);
    try {
      const { blob, fileName } = await downloadInsightListXlsx(token, scope, listType, {
        budgetYearId: fyId,
        causeId,
        days,
        search: query,
      });
      saveBlob(blob, fileName);
    } catch (e) {
      toast.error(`Could not export: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  }



  return (
    <PageContainer full>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-96">
          <Select items={listItems} value={listType} onValueChange={(v) => v && update({ list: String(v) })}>
            <SelectTrigger className="w-full" aria-label="Report">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LISTS.map((g) => (
                <SelectGroup key={g.group}>
                  <SelectLabel>{g.group}</SelectLabel>
                  {g.items.map((i) => (
                    <SelectItem key={i.value} value={i.value}>
                      {i.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>
        {meta.byFy && (
          <div className="w-36">
            <Select
              items={fyItems}
              value={String(fyId ?? currentFyId ?? "")}
              onValueChange={(v) => v && update({ fy: String(v) })}
              disabled={!fyItems.length}
            >
              <SelectTrigger className="w-full" aria-label="Financial year">
                <SelectValue placeholder="Financial year" />
              </SelectTrigger>
              <SelectContent>
                {fyItems.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {meta.byDays && (
          <div className="w-40">
            <Select items={dayItems} value={String(days)} onValueChange={(v) => v && update({ days: String(v) })}>
              <SelectTrigger className="w-full" aria-label="Window">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dayItems.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <CauseFilter className="w-full sm:w-56" />
        <div className="relative min-w-48 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this list…"
            className="pl-8"
          />
        </div>
        <Button variant="outline" onClick={exportExcel} disabled={!data || rows.length === 0 || exporting}>
          {exporting ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />} Export to Excel
        </Button>
      </div>

      <p className="-mt-2 text-sm text-muted-foreground">
        {scope?.operationUnit.name}
        {scope?.department ? ` · ${scope.department.name}` : ""} · {scope?.legalEntity.name}
        {data && (
          <>
            {" · "}
            {query ? `${rows.length} of ` : ""}
            {data.total.toLocaleString("en-IN")} {data.total === 1 ? "row" : "rows"}
            {data.truncated ? ` (showing first ${data.rows.length})` : ""}
          </>
        )}
      </p>

      <Busy busy={loading && !!data}>
      <Card className="py-0">
        <CardContent className="p-0">
          {error ? (
            <p className="p-8 text-center text-sm text-destructive">{error}</p>
          ) : !data ? (
            <BlockPlaceholder className="h-72 p-4" />
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nothing to show.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    {data.columns.map((c) => (
                      <th
                        key={c.key}
                        className={`px-3 py-2 font-medium whitespace-nowrap ${c.type === "amount" || c.type === "number" ? "text-right" : ""}`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0">
                      {data.columns.map((c) => (
                        <td
                          key={c.key}
                          className={`px-3 py-2 align-top ${c.type === "amount" || c.type === "number" ? "text-right tabular-nums whitespace-nowrap" : ""} ${c.type === "date" ? "whitespace-nowrap" : ""}`}
                        >
                          {c.role === "donor" ? (
                            <DonorLink
                              name={String(r[c.key] ?? "")}
                              donorId={r.donorId == null ? null : Number(r.donorId)}
                            />
                          ) : c.key === "mobile" ? (
                            <PhoneValue value={r[c.key] == null ? null : String(r[c.key])} />
                          ) : c.key === "email" ? (
                            <EmailValue value={r[c.key] == null ? null : String(r[c.key])} />
                          ) : (
                            renderCell(c, r[c.key])
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </Busy>
    </PageContainer>
  );
}

function renderCell(c: InsightColumn, v: string | number | null | undefined) {
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  switch (c.type) {
    case "amount":
      return formatAmount(Number(v));
    case "date":
      return formatDate(String(v));
    case "number":
      return Number(v).toLocaleString("en-IN");
    default:
      return String(v);
  }
}
